import {
  Badge,
  Button,
  DashboardIcon,
  MeetingActiveIcon,
  MeetingDefaultIcon,
  MenuIcon,
  MessagesActiveIcon,
  MessagesDefaultIcon,
  NotificationDefaultIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { NotificationsWrapper } from 'components/Header/components/Notifications/NotificationsWrapper';
import { type FC, type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayoutContext } from '../../context/AppLayoutContext';
import { useRunningPlannedMeetings } from '../../hooks/useCalendarEvents';
import { useCurrentView, useStackHandler } from '../../hooks/useStackHandler';
import { RoomType } from '../../matrix/constants';
import { useTotalUnreadCount } from '../../matrix/hooks/useNotifications';
import { MatrixChatFlyout } from '../MatrixChatFlyout/MatrixChatFlyout';
import { MeetingsFlyout } from '../MeetingFlyout/MeetingsFlyout';
import { MobileMenuFlyout } from '../MobileMenuFlyout/MobileMenuFlyout';
import styles from './MobileMenu.module.scss';

export const MobileMenuQueryFieldsDocument = graphql(`
  fragment MobileMenuQueryFields on Query {
    ...CalendarEventMapCalendarEventsToBpEventTypeFields
    ...MeetingsFlyoutFields
    ...UseDMRoomsFields  
  }
`);

type MobileMenuItemsType = {
  id: string;
  trigger: ReactNode;
  onClick: () => void;
};

type MobileMenuProps = {
  className?: string | undefined;
  query: FragmentType<typeof MobileMenuQueryFieldsDocument> | undefined;
};

export const MobileMenu: FC<MobileMenuProps> = ({ className, query }) => {
  const fragment = getFragmentData(MobileMenuQueryFieldsDocument, query);
  const navigate = useNavigate();

  const currentPath = useLocation();
  const { t } = useTranslation();

  const { runningMeetings } = useRunningPlannedMeetings(fragment);

  const stackHandler = useStackHandler();
  const currentView = useCurrentView();
  const unreadCount = useTotalUnreadCount({ includeRoomTypes: [RoomType.DM] });

  const items: MobileMenuItemsType[] = [
    {
      id: 'dashboard',
      trigger: (
        <div className={styles.trigger}>
          <Button hierarchy='ghost' icon={<DashboardIcon className={styles.icon} />} />
          <div className={styles.title}>{t('dashboard.title')}</div>
        </div>
      ),
      onClick: () => {
        navigate('/');
      },
    },
    {
      id: 'meetings',
      trigger: (
        <div className={styles.trigger}>
          <Button
            hierarchy='ghost'
            icon={
              runningMeetings.length > 0 ? (
                <MeetingActiveIcon className={styles.icon} />
              ) : (
                <MeetingDefaultIcon className={styles.icon} />
              )
            }
          />
          <div className={styles.title}>{t('meetings.titleShort')}</div>
        </div>
      ),
      onClick: () => {
        if (currentView?.id === 'meetings') {
          stackHandler.closeById('meetings');
          return;
        }
        stackHandler.push({
          id: 'meetings',
          title: t('meetings.titleShort'),
          component: <MeetingsFlyout query={fragment} />,
          group: 'meetings',
        });
      },
    },
    {
      id: 'menu',
      trigger: (
        <div className={styles.trigger}>
          <Button hierarchy='ghost' icon={<MenuIcon className={styles.icon} />} />
          <div className={styles.title}>{t('common.menu')}</div>
        </div>
      ),
      onClick: () => {
        if (currentView?.id === 'menu') {
          stackHandler.closeById('menu');
          return;
        }
        stackHandler.push({
          id: 'menu',
          title: t('common.menu'),
          component: <MobileMenuFlyout />,
          group: 'menu',
        });
      },
    },
    {
      id: 'messages',
      trigger: (
        <div className={styles.trigger}>
          <Button
            hierarchy='ghost'
            icon={
              unreadCount > 0 ? (
                <MessagesActiveIcon className={styles.icon} />
              ) : (
                <MessagesDefaultIcon className={styles.icon} />
              )
            }
          />
          <div className={styles.title}>{t('messages.title', { count: 2 })}</div>
        </div>
      ),
      onClick: () => {
        if (currentView?.id === 'messages') {
          stackHandler.closeById('messages');
          return;
        }

        stackHandler.push({
          id: 'messages',
          title: t('messages.title', { count: 2 }),
          component: <MatrixChatFlyout query={fragment} />,
          group: 'messages',
        });
      },
    },
    {
      id: 'notifications',
      trigger: (
        <div className={styles.trigger}>
          <Button hierarchy='ghost' icon={<NotificationDefaultIcon className={styles.icon} />} />
          <div className={styles.title}>{t('notifications.titleShort')}</div>
        </div>
      ),
      onClick: () => {
        if (currentView?.id === 'notifications') {
          stackHandler.closeById('notifications');
          return;
        }
        stackHandler.push({
          id: 'notifications',
          title: t('notifications.titleShort'),
          component: <NotificationsWrapper onClick={navigate} />,
          group: 'notifications',
        });
      },
    },
  ];

  const someActive = items.some(
    (item) =>
      (item.id && currentView?.id === item.id) ||
      (!currentView && currentPath.pathname === '/' && item.id === 'dashboard'),
  );

  return (
    <div className={classNames(styles['mobile-menu'], className)}>
      {items.map((item) => {
        const classes = classNames(styles['button-wrapper'], {
          [styles.active]:
            (item.id && currentView?.id === item.id) ||
            (!currentView && currentPath.pathname === '/' && item.id === 'dashboard') ||
            (!someActive && item.id === 'menu'),
        });
        return (
          <div key={item.id} className={classes} onClick={item.onClick}>
            {item.trigger}
          </div>
        );
      })}
    </div>
  );
};
